import React from 'react';
import { Box, Chip, Skeleton, TableCell, TableRow, Tooltip } from '@mui/material';
import { useAtom } from 'jotai';
import { Package, DeliveryScheduleTypes } from '../../types/packager-types';
import { LinkTableStyled } from '../../components/common/StyledComponents';
import PackageDelete from './package/PackageDelete';
import PackageDownload from './package/PackageDownload';
import PackageStatus from './package/PackageStatus';
import PackageVerify from './package/PackageVerify';
import PackageFreeze from './package/PackageFreeze';
import PackagePreview from './package/PackagePreview';
import PackageSend from './package/PackageSend';
import PackageReRender from './package/PackageReRender';
import PackageCreateFrom from './package/PackageCreateFrom';
import CompletenessChip from './completeness/CompletenessChip';
import {
  packagerIdForDocumentsModalAtom,
  packageEditDialogPackageIdAtom,
} from '../../atoms/packager-atoms';
import { useParams } from 'react-router';
import useGetPackager from '../hooks/packager/useGetPackager';
import { getNextRunTime, formatNextRunTime } from '../../utils/cronNextRun';
import { formatDistanceToNow } from 'date-fns';
export interface IPackageTableRowProps {
  p: Package;
}

const PackageTableRow: React.FC<IPackageTableRowProps> = ({ p }) => {
  const { dpid } = useParams<{ dpid: string }>();
  const { packager, isLoading, isFetching, isError } = useGetPackager(dpid!);

  const [, setPackagerIdForDocumentsModalAtom] = useAtom(packagerIdForDocumentsModalAtom);
  const [, setPackageEditDialogPackageId] = useAtom(packageEditDialogPackageIdAtom);

  const createdDate = new Date(p.created_at);
  const updatedDate = new Date(p.updated_at);

  const handleDocumentCountClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setPackagerIdForDocumentsModalAtom(p.id);
  };

  const handleStatusClick = () => {
    setPackageEditDialogPackageId(p.id);
  };

  if (isLoading) {
    return (
      <TableRow>
        <TableCell>
          <Skeleton
            animation="wave"
            variant="rounded"
            height={22}
            width={64}
            sx={{ borderRadius: '6px' }}
          />
        </TableCell>
        <TableCell align="center">
          <Skeleton
            animation="wave"
            variant="rounded"
            height={14}
            width={48}
            sx={{ borderRadius: '4px', mx: 'auto' }}
          />
        </TableCell>
        <TableCell align="center">
          <Skeleton
            animation="wave"
            variant="rounded"
            height={14}
            width={32}
            sx={{ borderRadius: '4px', mx: 'auto' }}
          />
        </TableCell>
        <TableCell align="center">
          <Skeleton
            animation="wave"
            variant="rounded"
            height={22}
            width={72}
            sx={{ borderRadius: '6px', mx: 'auto' }}
          />
        </TableCell>
        <TableCell align="center">
          <Skeleton
            animation="wave"
            variant="rounded"
            height={14}
            width={96}
            sx={{ borderRadius: '4px', mx: 'auto' }}
          />
        </TableCell>
        <TableCell align="center">
          <Skeleton
            animation="wave"
            variant="rounded"
            height={14}
            width={96}
            sx={{ borderRadius: '4px', mx: 'auto' }}
          />
        </TableCell>
        <TableCell align="center">
          <Skeleton
            animation="wave"
            variant="rounded"
            height={22}
            width={72}
            sx={{ borderRadius: '6px', mx: 'auto' }}
          />
        </TableCell>
        <TableCell align="right">
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <Skeleton animation="wave" variant="circular" width={18} height={18} />
            <Skeleton animation="wave" variant="circular" width={18} height={18} />
          </Box>
        </TableCell>
      </TableRow>
    );
  }

  if (isError) {
    return <div>Error loading packager data</div>;
  }

  return (
    <TableRow>
      <TableCell align="left">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <PackageStatus status={p.status!} onClick={handleStatusClick} />
          {p.is_stale && (
            <Tooltip
              placement="top"
              title={
                p.data_changed_at &&
                p.rendered_at &&
                new Date(p.data_changed_at) > new Date(p.rendered_at)
                  ? `Data behind this package changed ${formatDistanceToNow(
                      new Date(p.data_changed_at),
                      {
                        addSuffix: true,
                      }
                    )}. Re-render to pick it up.`
                  : 'Entries were edited since this package was rendered. Re-render to pick it up.'
              }
            >
              <Chip label="STALE" color="warning" size="small" data-cy={`chip-stale-${p.id}`} />
            </Tooltip>
          )}
          {p.is_stale === null && (
            <Tooltip
              placement="top"
              title="This package's build time is unknown, so it may be out of date. Re-render to check."
            >
              <Chip label="UNKNOWN" size="small" data-cy={`chip-stale-unknown-${p.id}`} />
            </Tooltip>
          )}
        </Box>
      </TableCell>
      <TableCell align="center">{p.id}</TableCell>
      <TableCell align="center">
        <LinkTableStyled to="#" onClick={handleDocumentCountClick}>
          {p?.document_data?.count?.toLocaleString() || '0'}
        </LinkTableStyled>
      </TableCell>
      <TableCell align="center">
        <CompletenessChip completeness={p.completeness} packageId={p.id} />
      </TableCell>
      <TableCell align="center">{createdDate.toLocaleString()}</TableCell>
      <TableCell align="center">{updatedDate.toLocaleString()}</TableCell>
      <TableCell align="center">
        {packager?.delivery_schedule_type === DeliveryScheduleTypes.MANUAL ? (
          <Tooltip
            title="Delivery will happen manually when the package is verified"
            placement="top"
          >
            <Chip label="MANUAL" color="warning" />
          </Tooltip>
        ) : packager?.delivery_schedule_type === DeliveryScheduleTypes.AUTOMATIC ? (
          <Tooltip
            title="Delivery will happen automatically when the package is verified"
            placement="top"
          >
            <Chip label="AUTOMATIC" color="success" />
          </Tooltip>
        ) : packager?.delivery_schedule_type === DeliveryScheduleTypes.CRON_SCHEDULE &&
          packager?.delivery_cron_expression ? (
          (() => {
            const nextRun = getNextRunTime(packager.delivery_cron_expression);
            return nextRun ? (
              <Tooltip title={`Next delivery: ${nextRun.toLocaleString()}`} placement="top">
                <Chip label={`CRON: ${formatNextRunTime(nextRun)}`} color="info" size="small" />
              </Tooltip>
            ) : (
              <Chip label="CRON SCHEDULE" color="info" size="small" />
            );
          })()
        ) : null}
      </TableCell>
      <TableCell align="right">
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <PackageFreeze packageId={p.id} packageStatus={p.status!} />
          <PackageVerify packageId={p.id} packageStatus={p.status!} />
          <PackageSend packagerId={packager?.id!} packageId={p.id} packageStatus={p.status!} />
          <PackageDownload packagerId={packager?.id!} packageId={p.id} />
          <PackageReRender
            packageId={p.id}
            packageStatus={p.status!}
            transmittedAt={p.transmitted_at}
            isStale={p.is_stale}
          />
          <PackageCreateFrom packageId={p.id} />
          <PackageDelete packageId={p.id} />
        </Box>
      </TableCell>
    </TableRow>
  );
};

export default PackageTableRow;
